import { defineStore } from 'pinia';
import { textShapes, nonTextShapes, useStore as useShapeStore } from './shape';
import { useStore as useProductStore } from './product';
import _findIndex from 'lodash.findindex';

import {
  LogoShape,
  TextShape,
} from '@/web-components/import-wizard-v2/typings/common';
import FileService from '@/services/FileService';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { useStore as useMirrorStore } from './mirror';
import { base64StringToBlob } from 'blob-util';

export const useStore = defineStore({
  id: 'colorManagement',

  state: () => {
    return {
      pmsColorsInUse: [] as PmsColor[],
      availablePmsColors: [] as PmsColor[],
      lastPalette: '',
    };
  },

  actions: {
    getPmsColorsOnPrintPosition(): PmsColor[] {
      const shapeStore = useShapeStore();
      const productStore = useProductStore();

      const shapes = shapeStore.shapes;

      const nonTextPrintPositionShapes = shapes
        .filter((shape) =>
          shapeStore.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      const allPmsColorsInArtworks = nonTextPrintPositionShapes.reduce<
        PmsColor[]
      >((prev, curr) => {
        return [...prev, ...curr.pmsColorsInPngFile];
      }, []);

      const textPrintPositionShapes = shapes
        .filter((shape) =>
          shapeStore.positionShapeMap.some(
            (psm) =>
              psm.id === productStore.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter(textShapes) as TextShape[];

      const textPmsColors =
        this.availablePmsColors.length > 0
          ? textPrintPositionShapes.map((text) => {
              return this.availablePmsColors.find(
                (c) => c.name === text.w2pText.colorName
              ) as PmsColor;
            })
          : [];

      // All the colours on the current print position
      const allPmsColors = [...allPmsColorsInArtworks, ...textPmsColors];

      const uniquePmsColors = [
        ...new Map(allPmsColors.map((item) => [item['name'], item])).values(),
      ];

      return uniquePmsColors;
    },
    resetColorsStateToDefault() {
      this.$patch((state) => {
        state.pmsColorsInUse = [] as PmsColor[];
        state.lastPalette = '';
      });
    },
    async getAvailablePmsColors() {
      try {
        const productStore = useProductStore();
        const shapesStore = useShapeStore();

        const selectedPrintPosition =
          productStore.unavailablePrintPositions.find(
            (x) => x.positionId === productStore.selectedPrintPosition.id
          );

        let isPaletteChanged = false;

        if (selectedPrintPosition?.technique.groupCode !== this.lastPalette) {
          const { data } = await FileService.getAvailablePmsColors(
            selectedPrintPosition?.technique.groupCode
          );
          this.availablePmsColors = data;
          this.lastPalette = selectedPrintPosition?.technique.groupCode ?? '';
          isPaletteChanged = true;
        }

        if (isPaletteChanged) {
          //Check all proposed colors, and propose again closest from new palette
          const allShapes = shapesStore.shapes;
          allShapes.forEach((shape) => {
            if (
              shapesStore.positionShapeMap.find((x) => x.logoId === shape.id)
                ?.id === selectedPrintPosition?.positionId
            ) {
              if (shape.kind === 'Logo') {
                this.changeToClosestPantonesFromPalette(shape);
              } else if (shape.kind === 'Text') {
                this.changeToClosestPantoneFromPalette(shape);
              }
            }
          });
        }

        return this.availablePmsColors;
      } catch (error) {
        console.log('An error occurred', error);
      }
    },
    changeToClosestPantonesFromPalette(shape: LogoShape) {
      shape.pmsColorsInPngFile.forEach((shapeColor) => {
        let smallestFuzzDistance = 100;
        if (
          this.availablePmsColors.find((x) => x.name === shapeColor.name) ===
          undefined
        ) {
          this.availablePmsColors.forEach((pantoneColor) => {
            const fuzzDistance = this.getFuzzDistance(
              shapeColor.red,
              pantoneColor.red,
              shapeColor.green,
              pantoneColor.green,
              shapeColor.blue,
              pantoneColor.blue
            );
            if (fuzzDistance < smallestFuzzDistance) {
              smallestFuzzDistance = fuzzDistance;
              shapeColor.name = pantoneColor.name;
            }
          });
        }
      });
    },
    changeColorsToClosestPantonesFromPalette(colors: PmsColor[]) {
      colors.forEach((color) => {
        let smallestFuzzDistance = 100;
        if (
          this.availablePmsColors.find((x) => x.name === color.name) ===
          undefined
        ) {
          this.availablePmsColors.forEach((pantoneColor) => {
            const fuzzDistance = this.getFuzzDistance(
              color.red,
              pantoneColor.red,
              color.green,
              pantoneColor.green,
              color.blue,
              pantoneColor.blue
            );
            if (fuzzDistance < smallestFuzzDistance) {
              smallestFuzzDistance = fuzzDistance;
              color.name = pantoneColor.name;
            }
          });
        }
      });
    },
    changeToClosestPantoneFromPalette(shape: TextShape) {
      let smallestFuzzDistance = 100;
      if (
        this.availablePmsColors.find(
          (x) => x.name === shape.w2pText.colorName
        ) === undefined
      ) {
        this.availablePmsColors.forEach((pantoneColor) => {
          let fuzzDistance = 0;
          fuzzDistance = this.getFuzzDistance(
            shape.w2pText.color[0] * 255,
            pantoneColor.red,
            shape.w2pText.color[1] * 255,
            pantoneColor.green,
            shape.w2pText.color[2] * 255,
            pantoneColor.blue
          );
          if (fuzzDistance < smallestFuzzDistance) {
            smallestFuzzDistance = fuzzDistance;
            shape.w2pText.colorName = pantoneColor.name;
          }
        });
      }
    },
    getFuzzDistance(
      r1: number,
      r2: number,
      g1: number,
      g2: number,
      b1: number,
      b2: number
    ) {
      return (
        (Math.sqrt(
          ((r1 - r2) * (r1 - r2) +
            (g1 - g2) * (g1 - g2) +
            (b1 - b2) * (b1 - b2)) /
            3
        ) /
          256) *
        100
      );
    },
    getPmsColorByPrintPositionId(printPositionId: string) {
      const shapeStore = useShapeStore();

      const shapes = shapeStore.shapes;

      // Extract logos from the shapes array
      const nonTextPrintPositionShapes = shapes
        .filter((shape) =>
          shapeStore.positionShapeMap.some(
            (psm) => psm.id === printPositionId && psm.logoId === shape.id
          )
        )
        .filter(nonTextShapes) as LogoShape[];

      // All colours from the logos
      const allPmsColorsInArtworks = nonTextPrintPositionShapes.reduce<
        PmsColor[]
      >((prev, curr) => {
        return [...prev, ...curr.pmsColorsInPngFile];
      }, []);

      // Extract texts from the shapes array
      const textPrintPositionShapes = shapes
        .filter((shape) =>
          shapeStore.positionShapeMap.some(
            (psm) => psm.id === printPositionId && psm.logoId === shape.id
          )
        )
        .filter(textShapes) as TextShape[];

      // All olours from the texts
      const textPmsColors = textPrintPositionShapes.map((text) => {
        return this.availablePmsColors.find(
          (c) => c.name === text.w2pText.colorName
        ) as PmsColor;
      });

      // All the colours on the current print position
      const allPmsColors = [...allPmsColorsInArtworks, ...textPmsColors];

      // Remove the "Closest Pantone" prefix when preparing colors for retreiving only the unique ones
      for (const pmsColor of allPmsColors) {
        if (pmsColor.name.includes('Closest Pantone:')) {
          const colorNameSplit = pmsColor.name.split(':');
          pmsColor.name = colorNameSplit[1].trimStart();
        }
      }

      const uniquePmsColors = [
        ...new Map(allPmsColors.map((item) => [item['name'], item])).values(),
      ];

      return uniquePmsColors;
    },
    getNumberOfColoursByPrintPositionId(printPositionId: string) {
      const productStore = useProductStore();

      const isLaserConverter = productStore.isLaserConverter(printPositionId);
      const isFullColorTechnique = productStore.isFullColor(printPositionId);
      const isDebossingTechnique =
        productStore.isDebossingTechnique(printPositionId);

      if (isFullColorTechnique) {
        return 8; // Full color techniques always counts as 8 color
      }

      if (isLaserConverter || isDebossingTechnique) {
        return 1; // Laser and Debossing always counts as 1 color
      }

      return this.getPmsColorByPrintPositionId(printPositionId).length;
    },
  },
});
